/**
 * DistrictMapsAdmin: staff publish the district maps residents can show as
 * reference layers -- the current adopted map, drafts under consideration,
 * and older maps -- and remove them again.
 *
 *   GET    /v1/tenant/reference-layers          (public list)
 *   POST   /v1/tenant/reference-layers          (staff: add or replace)
 *   DELETE /v1/tenant/reference-layers/{slug}   (staff)
 *
 * Files are GeoJSON FeatureCollections of Polygon/MultiPolygon districts,
 * each with a `name` or `number` property. The current map also powers
 * "start from current districts", so it must have one feature per district.
 */
import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiBaseUrl, fetchWithAuth } from '../../lib/api';
import { fetchReferenceLayers, type ReferenceLayer } from '../../lib/plans';
import { useTenantStore } from '../../stores/tenant';
import { TrashIcon } from '../icons';

type Kind = 'old_districts' | 'draft' | 'historical';

const KIND_LABELS: Record<Kind, string> = {
  old_districts: 'Current districts',
  draft: 'Draft map',
  historical: 'Previous map',
};

const KIND_HINTS: Record<Kind, string> = {
  old_districts: 'Replaces the current map. Also used for “start from current districts”.',
  draft: 'A map under consideration. Shown with a dashed line.',
  historical: 'An older map, for comparison.',
};

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

async function errorMessage(response: Response): Promise<string> {
  if (response.status === 401 || response.status === 403) {
    return 'Sign in with a staff account to publish district maps.';
  }
  try {
    const body = (await response.json()) as { detail?: unknown };
    const detail = body.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object') {
      const d = detail as { message?: string; problems?: string[] };
      const problems = Array.isArray(d.problems) ? `: ${d.problems.slice(0, 3).join('; ')}` : '';
      return `${d.message ?? 'The upload was rejected'}${problems}.`;
    }
  } catch {
    // fall through
  }
  return `The request failed (${response.status}).`;
}

function MapRow({ layer, onDeleted }: { layer: ReferenceLayer; onDeleted: () => void }): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const kind = (layer.kind in KIND_LABELS ? layer.kind : 'historical') as Kind;

  async function remove(): Promise<void> {
    setBusy(true);
    setError(null);
    const response = await fetchWithAuth(
      `${apiBaseUrl()}/v1/tenant/reference-layers/${encodeURIComponent(layer.slug)}`,
      { method: 'DELETE' },
    );
    setBusy(false);
    if (response.ok) onDeleted();
    else setError(await errorMessage(response));
  }

  return (
    <li className="flex flex-wrap items-start justify-between gap-3 py-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-ink">
          {layer.label}
          <span className="ml-2 rounded bg-stone-100 px-1.5 py-0.5 align-middle text-[10px] font-bold uppercase tracking-wide text-stone-600">
            {KIND_LABELS[kind]}
          </span>
        </p>
        <p className="mt-0.5 text-xs text-stone-500">
          {layer.geojson.features.length} district{layer.geojson.features.length === 1 ? '' : 's'}
          {layer.effectiveDate ? ` · ${formatDate(layer.effectiveDate)}` : ''}
          {layer.description ? ` · ${layer.description}` : ''}
        </p>
        {error && <p className="mt-1 text-xs text-red-700">{error}</p>}
      </div>
      {confirming ? (
        <span className="flex items-center gap-2 text-xs">
          <span className="text-stone-600">Remove this map?</span>
          <button type="button" className="btn-secondary h-8 px-2.5 text-xs" onClick={() => setConfirming(false)}>
            Keep
          </button>
          <button
            type="button"
            disabled={busy}
            className="h-8 rounded-lg bg-red-600 px-2.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
            onClick={() => void remove()}
          >
            {busy ? 'Removing…' : 'Remove'}
          </button>
        </span>
      ) : (
        <button
          type="button"
          className="btn-ghost h-8 px-2 text-xs text-stone-600"
          onClick={() => setConfirming(true)}
          aria-label={`Remove ${layer.label}`}
        >
          <TrashIcon size={14} /> Remove
        </button>
      )}
    </li>
  );
}

export function DistrictMapsAdmin(): JSX.Element {
  const tenant = useTenantStore((s) => s.tenant);
  const queryClient = useQueryClient();
  const maps = useQuery({
    queryKey: ['reference-layers', tenant?.slug],
    queryFn: fetchReferenceLayers,
    enabled: tenant !== null,
    retry: false,
  });

  const [kind, setKind] = useState<Kind>('draft');
  const [label, setLabel] = useState('');
  const [date, setDate] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  function refresh(): void {
    void queryClient.invalidateQueries({ queryKey: ['reference-layers'] });
    // The map's layer list comes from the tenant config.
    void queryClient.invalidateQueries({ queryKey: ['tenant'] });
  }

  async function upload(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setDone(null);
    if (!file) {
      setError('Choose a GeoJSON file.');
      return;
    }
    let geojson: unknown;
    try {
      geojson = JSON.parse(await file.text());
    } catch {
      setError('That file isn’t valid JSON. Export the map as GeoJSON and try again.');
      return;
    }
    if (!geojson || typeof geojson !== 'object' || (geojson as { type?: string }).type !== 'FeatureCollection') {
      setError('The file must be a GeoJSON FeatureCollection of district polygons.');
      return;
    }
    const name = label.trim() || (kind === 'old_districts' ? 'Current districts' : '');
    if (!name) {
      setError('Give the map a name, like “Draft B”.');
      return;
    }
    setBusy(true);
    const response = await fetchWithAuth(`${apiBaseUrl()}/v1/tenant/reference-layers`, {
      method: 'POST',
      body: JSON.stringify({
        kind,
        label: name,
        description: description.trim() || null,
        effective_date: date || null,
        geojson,
      }),
    });
    setBusy(false);
    if (!response.ok) {
      setError(await errorMessage(response));
      return;
    }
    setDone(`Published “${name}”. Residents can turn it on under Layers.`);
    setLabel('');
    setDate('');
    setDescription('');
    setFile(null);
    (event.target as HTMLFormElement).reset();
    refresh();
  }

  return (
    <section
      className="mt-6 rounded-lg bg-white p-6 shadow-sm ring-1 ring-stone-200"
      aria-labelledby="district-maps-title"
    >
      <h2 id="district-maps-title" className="text-lg font-semibold text-ink">
        District maps
      </h2>
      <p className="mt-1 text-sm text-stone-600">
        Maps residents can show under <strong>Layers</strong> while they draw: the current districts, drafts
        under consideration, and previous maps.
      </p>

      {maps.isLoading ? (
        <p className="mt-4 text-sm text-stone-500">Loading…</p>
      ) : maps.data && maps.data.length > 0 ? (
        <ul className="mt-3 divide-y divide-stone-100">
          {maps.data.map((layer) => (
            <MapRow key={layer.slug} layer={layer} onDeleted={refresh} />
          ))}
        </ul>
      ) : (
        <p className="mt-4 rounded-lg bg-stone-50 p-3 text-sm text-stone-500">No district maps published yet.</p>
      )}

      <form className="mt-5 grid gap-4 border-t border-stone-100 pt-5 sm:grid-cols-2" onSubmit={(e) => void upload(e)}>
        <h3 className="text-sm font-semibold text-ink sm:col-span-2">Publish a map</h3>
        <label className="block text-sm">
          <span className="font-medium text-stone-700">Type</span>
          <select className="field-input mt-1" value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
            {(Object.keys(KIND_LABELS) as Kind[]).map((k) => (
              <option key={k} value={k}>
                {KIND_LABELS[k]}
              </option>
            ))}
          </select>
          <span className="mt-1 block text-xs text-stone-500">{KIND_HINTS[kind]}</span>
        </label>
        <label className="block text-sm">
          <span className="font-medium text-stone-700">Name</span>
          <input
            className="field-input mt-1"
            value={label}
            maxLength={255}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={kind === 'old_districts' ? 'Current districts' : 'e.g. Draft B'}
          />
          <span className="mt-1 block text-xs text-stone-500">A map with the same name replaces the old one.</span>
        </label>
        <label className="block text-sm">
          <span className="font-medium text-stone-700">
            {kind === 'draft' ? 'Proposed on' : 'Adopted on'} <span className="font-normal text-stone-400">(optional)</span>
          </span>
          <input className="field-input mt-1" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-stone-700">GeoJSON file</span>
          <input
            className="mt-1 block w-full text-sm text-stone-600 file:mr-3 file:rounded-lg file:border-0 file:bg-stone-100 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-ink hover:file:bg-stone-200"
            type="file"
            accept=".geojson,.json,application/geo+json,application/json"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <span className="mt-1 block text-xs text-stone-500">
            District polygons, each with a <code>name</code> or <code>number</code> property.
          </span>
        </label>
        <label className="block text-sm sm:col-span-2">
          <span className="font-medium text-stone-700">
            Description <span className="font-normal text-stone-400">(optional)</span>
          </span>
          <input
            className="field-input mt-1"
            value={description}
            maxLength={500}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Keeps the waterfront neighborhoods together."
          />
        </label>
        <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? 'Publishing…' : 'Publish map'}
          </button>
          {error && (
            <p className="text-sm text-red-700" role="alert">
              {error}
            </p>
          )}
          {done && (
            <p className="text-sm text-emerald-700" role="status">
              {done}
            </p>
          )}
        </div>
      </form>
    </section>
  );
}
